import { APP_INITIALIZER, Injectable, Provider } from '@angular/core';
import { Actions } from '@ngxs/store';
import { ActionContext } from '@ngxs/store/src/actions-stream';
import { ActionClass } from '@platform-frontend/util';
import { Observable, throwError } from 'rxjs';
import { distinctUntilChanged, map, scan, shareReplay, startWith } from 'rxjs/operators';
import { ActionFilter } from './action-filter/action-filter';

@Injectable({
  providedIn: 'root'
})
export class LoadingIndicatorService {
  constructor(private actions$: Actions) {}

  private pendingActions$: Observable<ActionClass[]>;
  private anyIsPending$: Observable<boolean>;

  init(): void {
    this.pendingActions$ = this.actions$.pipe(
      scan<ActionContext, ActionClass[]>((pendingActions, action) => this.trackActions(pendingActions, action), []),
      startWith([]),
      shareReplay(1)
    );

    this.anyIsPending$ = this.pendingActions$.pipe(
      map(pendingActions => pendingActions.length > 0),
      distinctUntilChanged()
    );

    this.pendingActions$.subscribe();
  }

  private trackActions(pendingActions: ActionClass[], context: ActionContext): ActionClass[] {
    switch (context.status) {
      case 'DISPATCHED':
        return [...pendingActions, this.getActionFromContext(context)];
      case 'SUCCESSFUL':
      case 'CANCELED':
      case 'ERRORED':
        return pendingActions.filter(action => action !== this.getActionFromContext(context));
      default:
        throw new Error(`Unexpected action status: ${context.status}`);
    }
  }

  private getActionFromContext(context: ActionContext): ActionClass {
    const actionInstance = context.action;
    return Object.getPrototypeOf(actionInstance).constructor;
  }

  /**
   * Builds an observable that describes whether actions are currently pending.
   *
   * @param filter If an {@link ActionFilter} ist provided, the resulting observable will yield true when an
   * action matched by this filter is pending. Otherwise, the resulting observable will yield true when any
   * action is pending.
   */
  getIsPending(filter?: ActionFilter | null | undefined): Observable<boolean> {
    if (!this.pendingActions$) {
      return throwError(() => new Error('Loading indicator was not registered. Import LoadingIndicatorModule.forRoot().'));
    } else if (filter === undefined || filter === null) {
      return this.anyIsPending$;
    } else {
      return this.pendingActions$.pipe(
        map(pendingActions => filter.matchesAny(pendingActions)),
        distinctUntilChanged()
      );
    }
  }
}

export const LoadingIndicatorServiceInitializer: Provider = {
  provide: APP_INITIALIZER,
  useFactory: (loadingIndicatorService: LoadingIndicatorService) => () => loadingIndicatorService.init(),
  deps: [LoadingIndicatorService],
  multi: true
};
